// Header unificado para todos os modos do workspace de questões
// (Estudo, Prova, Prova Agendada, Resultado, Revisão).
//
// Por que centralizar: cada modo tinha um header próprio com layout, cores
// e botões duplicados — qualquer ajuste virava 4 lugares pra mexer.
// Aqui, o caller passa o `mode`, ícone, título, summary (chips/gauge/nota
// específicos do modo) e ações; o componente cuida do layout responsivo
// (grid de botões 2 cols mobile / 3 cols desktop, summary e timers
// controlados por uiPrefs no mobile).
//
// Posição na página deve ser SEMPRE a mesma: primeiro filho da main do
// workspace, com `space-y-4` abaixo. Não inverter.

import type { ComponentChildren } from "preact";
import type { LucideIcon } from "lucide-react";
import { ChevronUp, Eye, EyeOff, Timer, TimerOff } from "lucide-react";
import {
  uiPrefs,
  toggleHeaderMobileShowSummary,
  toggleHeaderMobileShowTimers
} from "./uiPrefs";

export type WorkspaceMode =
  | "study"
  | "prova"
  | "prova-agendada"
  | "resultado"
  | "revisao";

export type WorkspaceIconTone =
  | "blue"
  | "amber"
  | "violet"
  | "emerald"
  | "primary"
  | "neutral";

export interface WorkspaceHeaderAction {
  id: string;
  icon: LucideIcon;
  label: string;
  title?: string;
  onClick: (e: MouseEvent) => void;
  tone?: "neutral" | "amber" | "danger" | "primary";
  badge?: string;
  /** Esconder esse botão em telas pequenas (mobile). Para ações
   *  pouco frequentes que só fazem sentido no desktop. */
  hideOnMobile?: boolean;
}

export interface WorkspaceHeaderProps {
  mode: WorkspaceMode;
  icon: LucideIcon;
  iconTone?: WorkspaceIconTone;
  title: string;
  subtitle?: ComponentChildren;
  /** Slot do "resumo" — varia por modo: chips de status (Estudo), gauge
   *  de respondidas (Prova), nota grande (Resultado). Caller decide. */
  summary?: ComponentChildren;
  /** Linha de cronômetros (só modos de prova). Caller renderiza os
   *  chips já prontos. Comportamento mobile controlado por uiPrefs. */
  timers?: ComponentChildren;
  actions: WorkspaceHeaderAction[];
  /** Botão de minimizar/expandir cabeçalho (Prova compact mode). */
  compactToggle?: { compact: boolean; onClick: () => void };
}

const ICON_TONE_CLASS: Record<WorkspaceIconTone, string> = {
  blue: "bg-blue-500/15 text-blue-500 ring-blue-500/30 dark:text-blue-400",
  amber: "bg-amber-500/15 text-amber-600 ring-amber-500/30 dark:text-amber-400",
  violet: "bg-violet-500/15 text-violet-600 ring-violet-500/30 dark:text-violet-400",
  emerald:
    "bg-emerald-500/15 text-emerald-600 ring-emerald-500/30 dark:text-emerald-400",
  primary: "bg-primary/10 text-primary ring-primary/20",
  neutral: "bg-muted text-muted-foreground ring-border"
};

export function WorkspaceHeader({
  icon: Icon,
  iconTone = "primary",
  title,
  subtitle,
  summary,
  timers,
  actions,
  compactToggle
}: WorkspaceHeaderProps) {
  const showSummaryMobile = uiPrefs.value.headerMobileShowSummary;
  const showTimersMobile = uiPrefs.value.headerMobileShowTimers;

  // No mobile, o aluno escolhe nas preferências o que quer ver. No
  // desktop, sempre mostra summary e timers (se existirem).
  const summaryClass = summary
    ? `${showSummaryMobile ? "block" : "hidden"} md:block`
    : "hidden";
  const timersClass = timers
    ? `${showTimersMobile ? "flex" : "hidden"} md:flex`
    : "hidden";

  return (
    <header class="rounded-md border border-border bg-card/60 p-3 md:p-4 lg:p-5">
      <div class="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-stretch lg:gap-4">
        {/* Bloco 1: ícone + título. No desktop fica vertical-center pra
            não parecer alto demais quando há summary/timers grandes ao lado. */}
        <div class="flex min-w-0 items-start gap-3 lg:flex-1 lg:basis-72 lg:items-center lg:gap-4">
          <div
            class={`grid size-10 shrink-0 place-items-center rounded-md ring-1 lg:size-14 ${ICON_TONE_CLASS[iconTone]}`}
          >
            <Icon className="size-5 lg:size-7" />
          </div>
          <div class="min-w-0 flex-1">
            <h1 class="truncate text-base font-bold tracking-tight md:text-lg lg:text-2xl">
              {title}
            </h1>
            {subtitle && (
              <p class="mt-0.5 truncate text-xs text-muted-foreground md:text-sm lg:mt-1">
                {subtitle}
              </p>
            )}
          </div>
          {/* Botões de prefs mobile + minimizar — SÓ no mobile (lg:hidden).
              No desktop não faz sentido ocultar nada. */}
          <div class="flex shrink-0 items-center gap-1 lg:hidden">
            {summary && (
              <button
                type="button"
                onClick={toggleHeaderMobileShowSummary}
                title={
                  showSummaryMobile
                    ? "Ocultar resumo"
                    : "Mostrar resumo"
                }
                aria-label="Alternar resumo no mobile"
                aria-pressed={showSummaryMobile ? "true" : "false"}
                class={`grid size-8 place-items-center rounded-md transition-colors ${
                  showSummaryMobile
                    ? "text-primary hover:bg-accent"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground"
                }`}
              >
                {showSummaryMobile ? (
                  <Eye className="size-4" />
                ) : (
                  <EyeOff className="size-4" />
                )}
              </button>
            )}
            {timers && (
              <button
                type="button"
                onClick={toggleHeaderMobileShowTimers}
                title={
                  showTimersMobile
                    ? "Ocultar cronômetros"
                    : "Mostrar cronômetros"
                }
                aria-label="Alternar cronômetros no mobile"
                aria-pressed={showTimersMobile ? "true" : "false"}
                class={`grid size-8 place-items-center rounded-md transition-colors ${
                  showTimersMobile
                    ? "text-primary hover:bg-accent"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground"
                }`}
              >
                {showTimersMobile ? (
                  <Timer className="size-4" />
                ) : (
                  <TimerOff className="size-4" />
                )}
              </button>
            )}
            {compactToggle && (
              <button
                type="button"
                onClick={compactToggle.onClick}
                title={
                  compactToggle.compact
                    ? "Expandir cabeçalho"
                    : "Minimizar cabeçalho"
                }
                aria-label="Alternar cabeçalho compacto"
                class="grid size-8 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              >
                <ChevronUp
                  className={`size-4 transition-transform ${compactToggle.compact ? "rotate-180" : ""}`}
                />
              </button>
            )}
          </div>
        </div>

        {/* Bloco 2: summary. No desktop estica `self-stretch` pra
            ocupar a mesma altura do grid de botões (chips em 2x2 alinham
            visualmente com 2 linhas de botões). */}
        {summary && (
          <div class={`${summaryClass} lg:order-2 lg:self-stretch`}>
            {summary}
          </div>
        )}

        {/* Bloco 3: timers. */}
        {timers && (
          <div
            class={`${timersClass} flex-wrap items-center gap-2 lg:order-3 lg:self-stretch`}
          >
            {timers}
          </div>
        )}

        {/* Bloco 4: ações em grid. Colunas escolhidas pra evitar "órfãos"
            (linha solitária com 1 botão): 4→2x2, 5→3+2, 6→3x3 etc.
            min-w no desktop pra cards de ação não ficarem espremidos.
            Conta separadamente quantas ações são visíveis em cada
            breakpoint — `hideOnMobile` esconde via CSS mas a célula
            do grid sumia, gerando linha órfã. */}
        <div
          class={`grid gap-2 lg:order-4 lg:ml-auto lg:min-w-[18rem] lg:self-stretch ${gridColsForActions(
            actions.filter((a) => !a.hideOnMobile).length,
            actions.length
          )}`}
        >
          {actions.map((a) => (
            <ActionButton key={a.id} action={a} />
          ))}
        </div>
      </div>
    </header>
  );
}

// Escolhe o número de colunas do grid de ações pra evitar "linhas órfãs"
// (linha final com só 1 botão). Regra do usuário: 4→2x2 (não 3+1), 6→3x3
// (não 4+2). Para 5 botões, 3 cols dá 3+2 — desigual mas sem órfão.
//
// Mobile e desktop podem ter número de ações DIFERENTE (botões com
// `hideOnMobile` somem só no mobile). Por isso recebemos os dois e
// escolhemos colunas por breakpoint separadamente.
function gridColsForActions(mobileCount: number, desktopCount: number): string {
  return `${colsForCount(mobileCount, "mobile")} ${colsForCount(desktopCount, "lg")}`;
}

function colsForCount(n: number, bp: "mobile" | "lg"): string {
  const prefix = bp === "lg" ? "lg:" : "";
  switch (n) {
    case 1:
      return `${prefix}grid-cols-1`;
    case 2:
      return `${prefix}grid-cols-2`;
    case 3:
      return `${prefix}grid-cols-3`;
    case 4:
      return `${prefix}grid-cols-2`; // 2x2
    case 5:
      return `${prefix}grid-cols-3`; // 3+2
    case 6:
      return `${prefix}grid-cols-3`; // 3x2
    case 7:
      return `${prefix}grid-cols-4`; // 4+3
    case 8:
      return `${prefix}grid-cols-4`; // 4x2
    default:
      return `${prefix}grid-cols-3`;
  }
}

function ActionButton({ action }: { action: WorkspaceHeaderAction }) {
  const { icon: Icon, label, onClick, title, tone, badge, hideOnMobile } = action;
  // Estado default: ícone violeta (text-primary), botão neutro.
  // Estado "aberto/ativo" (tone="primary"): botão inteiro preenchido violeta.
  // âmbar / danger mantêm cores do tom no ícone e no hover.
  const toneStyles = {
    neutral: {
      btn: "border-border bg-background text-foreground hover:border-primary/60 hover:bg-accent",
      icon: "text-primary"
    },
    primary: {
      btn: "border-primary bg-primary text-primary-foreground hover:brightness-110",
      icon: "text-primary-foreground"
    },
    amber: {
      btn: "border-amber-500/50 bg-amber-500/10 text-amber-700 hover:bg-amber-500/20 dark:text-amber-400",
      icon: "text-amber-600 dark:text-amber-400"
    },
    danger: {
      btn: "border-red-500/40 bg-red-500/5 text-red-600 hover:bg-red-500/15 dark:text-red-400",
      icon: "text-red-500 dark:text-red-400"
    }
  } as const;
  const t = toneStyles[tone ?? "neutral"];
  return (
    <button
      type="button"
      onClick={onClick}
      title={title ?? label}
      class={`${hideOnMobile ? "hidden md:inline-flex" : "inline-flex"} relative h-10 w-full items-center justify-start gap-2 rounded-md border px-3 text-xs font-medium transition-colors md:text-sm ${t.btn}`}
    >
      <Icon className={`size-4 shrink-0 ${t.icon}`} strokeWidth={2.25} />
      <span class="min-w-0 flex-1 truncate text-left">{label}</span>
      {badge && (
        <span class="absolute -right-1.5 -top-1.5 grid min-w-[18px] place-items-center rounded-full bg-amber-500 px-1 text-[10px] font-bold leading-none text-white shadow ring-2 ring-card">
          {badge}
        </span>
      )}
    </button>
  );
}
